/**
 * ConsistencyCalendar - GitHub-style P&L heatmap.
 *
 * Gold v2 phase 4b: the grid (New York weeks, Monday to Friday), each day's
 * P&L from gold.portfolio_daily, its colour level and the streaks are built by
 * the backend (gold_history_read.calendar). A day without a session row is
 * blank ("no data"), never $0. The browser lays the cells out.
 */

import React from 'react';
import { CalendarBlock, CalendarDay } from '../../types/performance';
import { fmtUsdShortSigned, fmtDay, fmtDayShort, MONTH_LABELS } from '../../utils/performanceFormat';

interface ConsistencyCalendarProps {
  calendar: CalendarBlock;
  showLegend?: boolean;
  compact?: boolean;
}

// Signed P&L only: gain ramp up, loss ramp down; brighter = bigger. Mirrors
// the trader-theme --tk-gain / --tk-loss tokens.
const EMPTY = '#1d1e23';
const FLAT = '#2a2c31';
const GAIN_RAMP = ['#2c5243', '#3f8a69', '#5cc79a'];
const LOSS_RAMP = ['#5a3934', '#9a5f56', '#e08a7e'];
const LABEL = '#6f747c';
const MOBILE_WEEKS = 8;

const colorOf = (level: number | null): string => {
  if (level == null) return EMPTY;
  if (level === 0) return FLAT;
  return level > 0 ? GAIN_RAMP[level - 1] : LOSS_RAMP[-level - 1];
};

const titleOf = (day: CalendarDay): string =>
  `${fmtDay(day.date)}\n${day.pnl == null ? 'No data' : `P&L: ${fmtUsdShortSigned(day.pnl)}`}`;

const ConsistencyCalendar: React.FC<ConsistencyCalendarProps> = ({ calendar, showLegend = true, compact = false }) => {
  if (!calendar || calendar.weeks.length === 0) {
    return null;
  }
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const weeks = isMobile ? calendar.weeks.slice(-MOBILE_WEEKS) : calendar.weeks;
  const firstShown = calendar.weeks.length - weeks.length;
  const monthLabels = calendar.months
    .filter(m => m.week_index >= firstShown)
    .map(m => ({ label: MONTH_LABELS[Number(m.month.slice(5, 7)) - 1], weekIdx: m.week_index - firstShown }));

  const cellSize = compact ? 10 : 11;
  const cellGap = 2;
  const leftPadding = 28;
  const topPadding = 20;
  const chartWidth = leftPadding + (weeks.length * (cellSize + cellGap));
  const chartHeight = topPadding + (5 * (cellSize + cellGap)) + 20;

  const mobileCellSize = compact ? 32 : 36;
  const mobileCellGap = 4;
  const mobileLeftPadding = 44;
  const mobileTopPadding = 28;
  const mobileChartWidth = mobileLeftPadding + (5 * (mobileCellSize + mobileCellGap)) + 10;
  const mobileChartHeight = mobileTopPadding + (weeks.length * (mobileCellSize + mobileCellGap)) + 10;

  return (
    <div className={`perf-cal ${compact ? 'is-compact' : ''}`}>
      <div className="perf-cal-h">
        <h3>Consistency Calendar</h3>
        <small>
          {calendar.streak_current > 0 && <b>{calendar.streak_current} day streak</b>}
          {weeks.length} weeks
        </small>
      </div>

      {!isMobile && (
        <svg width="100%" viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
          {['M', 'T', 'W', 'T', 'F'].map((day, i) => (
            <text key={`day-${i}`} x={10} y={topPadding + i * (cellSize + cellGap) + cellSize - 2} fill={LABEL} fontSize="9" textAnchor="middle">
              {i % 2 === 0 ? day : ''}
            </text>
          ))}
          {monthLabels.map(({ label, weekIdx }) => (
            <text key={`month-${weekIdx}`} x={leftPadding + weekIdx * (cellSize + cellGap)} y={12} fill={LABEL} fontSize="9">{label}</text>
          ))}
          {weeks.map((week, weekIdx) =>
            week.map((day, dayIdx) => (
              <rect
                key={day.date}
                x={leftPadding + weekIdx * (cellSize + cellGap)}
                y={topPadding + dayIdx * (cellSize + cellGap)}
                width={cellSize}
                height={cellSize}
                rx={1}
                fill={colorOf(day.level)}
                style={{ cursor: 'pointer' }}
              >
                <title>{titleOf(day)}</title>
              </rect>
            ))
          )}
        </svg>
      )}

      {isMobile && (
        <div style={{ width: '100%', overflow: 'hidden' }}>
          <svg width="100%" viewBox={`0 0 ${mobileChartWidth} ${mobileChartHeight}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', maxWidth: '100%' }}>
            {['M', 'T', 'W', 'T', 'F'].map((day, i) => (
              <text key={`day-header-${i}`} x={mobileLeftPadding + i * (mobileCellSize + mobileCellGap) + mobileCellSize / 2} y={16} fill={LABEL} fontSize="12" textAnchor="middle">
                {day}
              </text>
            ))}
            {[...weeks].reverse().map((week, rowIdx) => (
              <g key={`week-${rowIdx}`}>
                <text x={4} y={mobileTopPadding + rowIdx * (mobileCellSize + mobileCellGap) + mobileCellSize / 2 + 4} fill={LABEL} fontSize="9">
                  {fmtDayShort(week[0]?.date)}
                </text>
                {week.map((day, dayIdx) => (
                  <rect
                    key={day.date}
                    x={mobileLeftPadding + dayIdx * (mobileCellSize + mobileCellGap)}
                    y={mobileTopPadding + rowIdx * (mobileCellSize + mobileCellGap)}
                    width={mobileCellSize}
                    height={mobileCellSize}
                    rx={2}
                    fill={colorOf(day.level)}
                    style={{ cursor: 'pointer' }}
                  >
                    <title>{titleOf(day)}</title>
                  </rect>
                ))}
              </g>
            ))}
          </svg>
        </div>
      )}

      {showLegend && (
        <div className="perf-cal-legend">
          <div>
            <span>Less</span>
            {[EMPTY, ...GAIN_RAMP].map((color, i) => <i key={i} style={{ background: color }} />)}
            <span>More income</span>
          </div>
          {calendar.streak_best > 0 && (
            <span>Best streak: <span className="n">{calendar.streak_best}</span> days</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsistencyCalendar;
